// Inferred scheduling — the schedule follows what the store actually does.
//
// A template's schedule used to be something you had to declare up front, in a
// setup step, before you were allowed to count. It is now derived from the act
// of counting: every time a run is marked Complete, the completion date is
// compared against the template's current schedule and, when the two disagree,
// the schedule is rewritten to match the behaviour that actually happened.
//
//   Count on a Tuesday with no schedule at all      -> Every Tue
//   Complete again on the next Tuesday              -> consistent, left alone
//   Complete 30 days after that Tuesday instead     -> monthly on that date
//   Complete 8 days after                           -> every 8 days
//
// The user can still open the schedule editor and set anything they like; the
// next off-schedule completion is what moves it again. Two rules bound that:
//
//   * NRF fiscal calendars (4-5-4 / 5-4-4 / 4-4-5) are never rewritten. They
//     deliberately land on a different weekday AND a different day-of-month
//     every period, so "the day doesn't match" is their normal state, and the
//     choice of fiscal calendar is an accounting decision rather than a habit.
//   * Everything else — including the {preset:'none', skipped:true} sentinel
//     that used to mean "No schedule, I'll start manually" — is fair game.
//
// This module is pure: dates in, cycle out. Reading the template, finding the
// previous completion and writing the result live in
// services/inferredScheduleService.js.

import { DAY_BITS, JS_DAY_TO_BIT, toLocalDateStr } from '../components/cycleHelpers.mjs';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Accounting calendars the inference must never overwrite.
export const FISCAL_PRESETS = ['eop_454', 'eop_544', 'eop_445'];

// A completion gap in this band reads as "monthly". It has to be a band: a
// day-of-month cadence produces 28-31 day gaps depending on the month, and no
// exact number would ever match. 28 is deliberately excluded — that is exactly
// four weeks, the cadence the store setup wizard calls "every 4 weeks", and a
// store on that rhythm hits 28 every single time.
const MONTHLY_MIN_GAP = 29;
const MONTHLY_MAX_GAP = 31;

/** Parse a 'YYYY-MM-DD' string (or Date, or ISO timestamp) into a local Date at midnight. */
export function toLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const str = String(value);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(str);
  // A bare date string is parsed as local midnight, never UTC — 'T00:00:00'
  // keeps '2026-08-04' on the 4th west of Greenwich instead of the 3rd.
  const d = dateOnly ? new Date(`${str}T00:00:00`) : new Date(str);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Monday-first weekday name for a date-ish value ('Tuesday'), or '' if unparseable. */
export function weekdayNameOf(value) {
  const d = toLocalDate(value);
  if (!d) return '';
  // JS_DAY_TO_BIT maps getDay() to the same bit DAY_BITS uses, so this stays
  // in step with the rest of the cycle code rather than re-deriving an order.
  const bit = JS_DAY_TO_BIT[d.getDay()];
  return DAY_NAMES.find((name) => DAY_BITS[name] === bit) || '';
}

/** Whole days between two date-ish values, or null when either is missing/unparseable. */
export function daysBetween(earlier, later) {
  const a = toLocalDate(earlier);
  const b = toLocalDate(later);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

function isLastDayOfMonth(d) {
  return d.getDate() === new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/** True when this cycle is an NRF fiscal accounting calendar. */
export function isFiscalCycle(cycle) {
  return !!cycle && FISCAL_PRESETS.includes(cycle.preset);
}

/**
 * True when the cycle carries a real, usable recurrence. The
 * {preset:'none', skipped:true} sentinel and an empty weekday list both read as
 * "nothing to be consistent with".
 */
export function hasRealCycle(cycle) {
  if (!cycle || !cycle.preset || cycle.preset === 'none') return false;
  if ((cycle.preset === 'weekly_choice' || cycle.preset === 'twice_weekly')
      && !(Array.isArray(cycle.days) && cycle.days.length > 0)) {
    return false;
  }
  return true;
}

/**
 * Does `cycle` fire on `dateValue`?
 *
 * Weekday and day-of-month cycles answer this from the date alone. Interval
 * cycles that name no weekday ("every 3 days", "every 2 weeks" with no day
 * picked) have nothing on the date to check, so they are judged against
 * `gapDays` — the distance from the previous completion — and count as
 * consistent when there is no previous completion to contradict them.
 *
 * Returns true for a fiscal calendar: those are never rewritten, so the answer
 * that keeps them is the correct one.
 */
export function isCycleConsistentWith(cycle, dateValue, gapDays = null) {
  if (!hasRealCycle(cycle)) return false;
  if (isFiscalCycle(cycle)) return true;

  const d = toLocalDate(dateValue);
  if (!d) return true; // unreadable date changes nothing
  const weekday = weekdayNameOf(d);
  const preset = cycle.preset;

  if (preset === 'every_day') return true;

  if (preset === 'weekly_choice' || preset === 'twice_weekly') {
    return (cycle.days || []).includes(weekday);
  }

  if (preset === 'monthly_1') return d.getDate() === 1;
  if (preset === 'monthly_15') return d.getDate() === 15;
  if (preset === 'monthly_last') return isLastDayOfMonth(d);

  if (preset === 'custom') {
    const interval = Math.max(1, parseInt(cycle.interval, 10) || 1);
    const unit = cycle.unit || 'weeks';
    if (unit === 'days') return gapDays === null ? true : gapDays === interval;
    if (unit === 'weeks') {
      const days = Array.isArray(cycle.weekDays) ? cycle.weekDays : [];
      if (days.length > 0) return days.includes(weekday);
      return gapDays === null ? true : gapDays === interval * 7;
    }
    if (unit === 'months') {
      if ((cycle.monthType || 'day') === 'day') {
        const wanted = Math.max(1, Math.min(31, cycle.monthDay || 1));
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        return d.getDate() === Math.min(wanted, lastDay);
      }
      const pos = cycle.ordinalPos || 'first';
      const wantedWeekday = cycle.ordinalWeekday || 'Monday';
      if (weekday !== wantedWeekday) return false;
      const nth = Math.ceil(d.getDate() / 7);
      if (pos === 'last') return d.getDate() + 7 > new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      return nth === ({ first: 1, second: 2, third: 3 }[pos] || 1);
    }
    return true;
  }

  // Single-weekday presets: every_monday … every_sunday.
  if (preset.startsWith('every_')) {
    const name = preset.slice(6);
    const cap = name.charAt(0).toUpperCase() + name.slice(1);
    if (DAY_NAMES.includes(cap)) return weekday === cap;
  }

  // An unrecognised preset is left alone rather than rewritten out from under
  // whoever added it.
  return true;
}

/** Weekly-on-this-weekday, the shape the CycleSelector produces for one day. */
export function weeklyCycleFor(dateValue) {
  const weekday = weekdayNameOf(dateValue);
  if (!weekday) return null;
  return { preset: `every_${weekday.toLowerCase()}`, days: [weekday] };
}

/** Monthly-on-this-day-of-month, preferring the named presets where they fit. */
export function monthlyCycleFor(dateValue) {
  const d = toLocalDate(dateValue);
  if (!d) return null;
  if (isLastDayOfMonth(d)) return { preset: 'monthly_last' };
  if (d.getDate() === 1) return { preset: 'monthly_1' };
  if (d.getDate() === 15) return { preset: 'monthly_15' };
  return { preset: 'custom', interval: 1, unit: 'months', monthType: 'day', monthDay: d.getDate() };
}

/**
 * Turn an observed gap between two completions into a cycle landing on
 * `dateValue`. Exact matches only — an 8-day gap is "every 8 days", not a
 * rounded-off week — because the user reads the label back and it has to say
 * what they did.
 */
export function cycleFromGap(gapDays, dateValue) {
  const weekday = weekdayNameOf(dateValue);
  if (!weekday) return null;
  const gap = Number(gapDays);
  if (!Number.isFinite(gap) || gap <= 0) return null;

  if (gap === 1) return { preset: 'every_day' };
  if (gap === 7) return weeklyCycleFor(dateValue);
  if (gap === 14 || gap === 21 || gap === 28) {
    return { preset: 'custom', interval: gap / 7, unit: 'weeks', weekDays: [weekday] };
  }
  if (gap >= MONTHLY_MIN_GAP && gap <= MONTHLY_MAX_GAP) return monthlyCycleFor(dateValue);
  return { preset: 'custom', interval: gap, unit: 'days' };
}

/**
 * The whole rule, in one call.
 *
 * @param {object|null} currentCycle       the template's ordering_cycle_config
 *                                         (or the cycle it inherits from the store)
 * @param {string|Date} completedOn        the date this run was marked Complete
 * @param {string|Date|null} previousCompletedOn  the previous completion in the
 *                                         same template group, if any
 * @returns {{cycle: object, reason: string, gapDays: number|null}|null}
 *          the schedule to write, or null to leave the schedule as it is.
 */
export function inferScheduleOnComplete({ currentCycle, completedOn, previousCompletedOn = null } = {}) {
  const day = toLocalDate(completedOn);
  if (!day) return null;

  // Accounting calendars are an accounting decision, not a habit.
  if (isFiscalCycle(currentCycle)) return null;

  const gapDays = previousCompletedOn ? daysBetween(previousCompletedOn, day) : null;
  const usableGap = Number.isFinite(gapDays) && gapDays > 0 ? gapDays : null;

  const scheduled = hasRealCycle(currentCycle);
  if (scheduled && isCycleConsistentWith(currentCycle, day, usableGap)) return null;

  // Off-schedule, or never scheduled: model the gap when there is one to
  // measure, otherwise this weekday, every week.
  const inferred = (usableGap !== null ? cycleFromGap(usableGap, day) : null) || weeklyCycleFor(day);
  if (!inferred) return null;

  // Don't churn the row (or the "Updated since last count" banner that reads
  // updated_at) when the answer is the schedule already stored.
  if (sameCycle(inferred, currentCycle)) return null;

  return {
    cycle: inferred,
    reason: scheduled ? 'off_schedule' : 'no_schedule',
    gapDays: usableGap,
  };
}

/** Shallow structural comparison of two cycles, ignoring key order. */
export function sameCycle(a, b) {
  if (!a || !b) return false;
  if (a.preset !== b.preset) return false;
  const norm = (c) => JSON.stringify({
    preset: c.preset,
    days: [...(c.days || [])].sort(),
    weekDays: [...(c.weekDays || [])].sort(),
    interval: c.interval ?? null,
    unit: c.unit ?? null,
    monthType: c.monthType ?? null,
    monthDay: c.monthDay ?? null,
    ordinalPos: c.ordinalPos ?? null,
    ordinalWeekday: c.ordinalWeekday ?? null,
  });
  return norm(a) === norm(b);
}

/** Local YYYY-MM-DD for a Date — re-exported so callers need one import. */
export { toLocalDateStr };
